"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Nothing = exports.Just = exports.MaybePromise = exports.Maybe = void 0;
const result_1 = require("./result");
const promiseExecutor_1 = require("./promiseExecutor");
/**
 * Represent the result of an operation that may or may not return a
 * value
 */
var Maybe;
(function (Maybe) {
    function just(value) {
        return new Just(value);
    }
    Maybe.just = just;
    function nothing() {
        return new Nothing();
    }
    Maybe.nothing = nothing;
    /**
     * Returns a `Just` with the value given if it is not `null` or `undefined`.
     * Returns `Nothing` otherwise
     */
    function fromOptional(value) {
        return value === undefined || value === null ? Maybe.nothing() : Maybe.just(value);
    }
    Maybe.fromOptional = fromOptional;
    /**
     * This is here because the `Maybe` type does not have a constructor that
     * can be used with `instanceof`
     */
    function isMaybe(value) {
        return value instanceof Just || value instanceof Nothing;
    }
    Maybe.isMaybe = isMaybe;
    Maybe.orThrow = (maybe) => maybe.orThrow();
})(Maybe = exports.Maybe || (exports.Maybe = {}));
class MaybePromise extends Promise {
    /**
     * Create an `AsyncMaybe` from a `Promise` containing a `Maybe`
     */
    static from(promise) {
        return promiseExecutor_1.extendedPromise(MaybePromise, promise);
    }
    constructor(executor) {
        super(executor);
    }
    /**
     * Because this function is asynchronous, it cannot narrow the type of the
     * `AsyncMaybe`. Consider using `.then` or `await`-ing instead
     */
    isJust() {
        return this.then((maybe) => maybe.isJust());
    }
    /**
     * Because this function is asynchronous, it cannot narrow the type of the
     * `AsyncMaybe`. Consider using `.then` or `await`-ing instead
     */
    isNothing() {
        return this.then((maybe) => maybe.isNothing());
    }
    /**
     * Transforms the value inside a `Just`, or returns the `Nothing` if called
     * on `Nothing`.
     *
     * The function provided can also return a `Promise`, and that promise will
     * be resolved as required
     */
    map(fn) {
        return this.promiseChain((maybe) => __awaiter(this, void 0, void 0, function* () { return maybe.isJust() ? Maybe.just(yield fn(maybe.value)) : Maybe.nothing(); }));
    }
    /**
     * Calls the predicate with the value in the `Just`. Returns the `Just` if
     * the predicate is true, or a `Nothing` otherwise. If `filter` is called on
     * a `Nothing` it will return `Nothing`.
     *
     * The function provided can also return a `Promise`, and that promise will
     * be resolved as required
     */
    filter(predicate) {
        return this.promiseChain((maybe) => __awaiter(this, void 0, void 0, function* () {
            return maybe.isNothing()
                ? Maybe.nothing()
                : (yield predicate(maybe.value))
                    ? Maybe.just(maybe.value)
                    : Maybe.nothing();
        }));
    }
    /**
     * The monadic `bind`.
     *
     * Allows the chaining functions that return `Maybe`s.
     * Returns the result of the function, or `Nothing` if called on `Nothing`
     *
     * The function provided can also return a `Promise`, and that promise will
     * be resolved as required
     */
    chain(fn) {
        return this.promiseChain((maybe) => __awaiter(this, void 0, void 0, function* () { return (maybe.isJust() ? fn(maybe.value) : Maybe.nothing()); }));
    }
    /**
     * Either resolve to the value of the `Just` or reject the promise if called
     * on `Nothing`. Consider using `toResult` instead
     */
    orThrow(error) {
        return this.then((maybe) => maybe.orThrow(error));
    }
    /**
     * Either resolve to the value of the `Just`, or the value given.
     *
     * The value provided can also be a `Promise`, and that promise will
     * be resolved as required
     */
    withDefault(value) {
        return __awaiter(this, void 0, void 0, function* () {
            const defaultValue = yield value;
            return this.then((maybe) => maybe.withDefault(defaultValue));
        });
    }
    /**
     * Either resolve to the value of the `Just`, or the value returned by the
     * function given.
     */
    orElseDo(fn) {
        return this.then((maybe) => (maybe.isJust() ? maybe.value : fn()));
    }
    /**
     * Returns an `Ok` with the value inside the `Just` or an `Err` if called
     * on a `Nothing`. This returns an `AsyncResult`
     */
    toResult(error) {
        return result_1.ResultPromise.from(this.then((result) => result.toResult(error)));
    }
    promiseChain(fn) {
        return new MaybePromise((resolve, reject) => {
            this.then((maybe) => resolve(fn(maybe)), reject);
        });
    }
    toUnion(other) {
        return this.then((maybe) => maybe.toUnion(other));
    }
}
exports.MaybePromise = MaybePromise;
class Just {
    constructor(value) {
        this.value = value;
    }
    isJust() {
        return true;
    }
    isNothing() {
        return false;
    }
    /** Transforms the value inside a `Just`, or returns the `Nothing` if called on an `Nothing` */
    map(fn) {
        return Maybe.just(fn(this.value));
    }
    /**
     * Calls the predicate with the value in the `Just`. Returns the `Just` if the predicate is true,
     * or a `Nothing` otherwise. If `filter` is called on a `Nothing` it will return `Nothing`
     */
    filter(predicate) {
        return predicate(this.value) ? Maybe.just(this.value) : Maybe.nothing();
    }
    /**
     * The monadic `bind`.
     *
     * Allows the chaining functions that return `Maybe`s.
     * Returns the result of the function, or `Nothing` if called on `Nothing`
     */
    chain(fn) {
        return fn(this.value);
    }
    /**
     * Either return the value of the `Just` or throw an error if called on `Nothing`
     * This is here mainly for testing. Consider using:
     *
     * - an if statement with `.isJust` or `.isNothing`, or;
     * - `toResult`
     *
     * in other situations
     */
    orThrow(_) {
        return this.value;
    }
    /** Either return the value of the `Just`, or the value given */
    withDefault(_) {
        return this.value;
    }
    /**
     * Either return to the value of the `Just`, or the value returned by the
     * function given. Also consider using an if-statement with `isJust` or
     * `isNothing`.
     */
    orElseDo(_) {
        return this.value;
    }
    /**
     * Returns an `Ok` with the value inside the `Just` or an `Err` if called
     * on a `Nothing`
     */
    toResult(_) {
        return result_1.Result.ok(this.value);
    }
    toUnion(_) {
        return this.value;
    }
}
exports.Just = Just;
class Nothing {
    isJust() {
        return false;
    }
    isNothing() {
        return true;
    }
    /** Transforms the value inside a `Just`, or returns the `Nothing` if called on an `Nothing` */
    map(_) {
        return Maybe.nothing();
    }
    /**
     * Calls the predicate with the value in the `Just`. Returns the `Just` if the predicate is true,
     * or a `Nothing` otherwise. If `filter` is called on a `Nothing` it will return `Nothing`
     */
    filter(_) {
        return Maybe.nothing();
    }
    /**
     * The monadic `bind`.
     *
     * Allows the chaining functions that return `Maybe`s.
     * Returns the result of the function, or `Nothing` if called on `Nothing`
     */
    chain(_) {
        return Maybe.nothing();
    }
    /**
     * Either return the value of the `Just` or throw an error if called on `Nothing`
     * This is here mainly for testing. Consider using:
     *
     * - an if statement with `.isJust` or `.isNothing`, or;
     * - `toResult`
     *
     * in other situations
     */
    orThrow(error = 'Attempted to extract a value from a Nothing') {
        throw typeof error === 'string' ? new Error(error) : error;
    }
    /** Either return the value of the `Just`, or the value given */
    withDefault(value) {
        return value;
    }
    /**
     * Either return to the value of the `Just`, or the value returned by the
     * function given. Also consider using an if-statement with `isJust` or
     * `isNothing`.
     */
    orElseDo(fn) {
        return fn();
    }
    /**
     * Returns an `Ok` with the value inside the `Just` or an `Err` if called
     * on a `Nothing`
     */
    toResult(error) {
        return result_1.Result.err(error);
    }
    toUnion(other) {
        return other;
    }
}
exports.Nothing = Nothing;
//# sourceMappingURL=maybe.js.map